//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'

//#endregion

import { TypeHelper } from '../../../../helpers'

import { IMRTabController } from '../tab/tab.controller'
import { TabItem } from '../tab/tab.interfaces'

import { IIMRTabs, IMRTabsOptions, IMRTabsState, IMRTabsTabSelectingArgs } from './tabs.interfaces'


export class IMRTabsController
    implements
    IIMRTabs,
    ng.IComponentController {

    //#region Members

    public options: IMRTabsOptions = null
    public tabControllers: IMRTabController[] = [];

    private tabCount: number = 0
    private isInitializedDeferred: JQueryDeferred<void> = $.Deferred<void>()
    private isPostLinkDeferred: JQueryDeferred<void> = $.Deferred<void>()

    //#endregion


    constructor() {
        $
            .when(
                this.isPostLinkDeferred.promise(),
                this.isInitializedDeferred.promise()
            )
            .then(() => {
                this.initialize()
            })
    }


    //#region IComponentController Implementation 

    public $onChanges(changes: { [property: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.isInitializedDeferred.resolve()
        }
    }

    public $postLink(): void {
        this.isPostLinkDeferred.resolve()
    }

    //#endregion


    //#region Component

    private initialize(): void {
        this.initializeTabs()
            .then(() => {
                this.onInitialized()
            })
    }

    //#endregion


    //#region Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized({
                component: this
            })
        }
    }

    private onStateStoring(state: IMRTabsState): void {
        if (this.options != null && _.isFunction(this.options.onStateStoring)) {
            this.options.onStateStoring(state)
        }
    }

    private onStateRestoring(): IMRTabsState {
        if (this.options != null && _.isFunction(this.options.onStateRestoring)) {
            return this.options.onStateRestoring()
        }

        return null
    }

    private onTabSelecting(currentTabName: string, targetTabName: string): { cancel?: boolean, cancelAsync: JQueryPromise<boolean> } {
        if (this.options != null && _.isFunction(this.options.onTabSelecting)) {
            const eventArgs: IMRTabsTabSelectingArgs = {
                component: this,
                currentTabName,
                targetTabName,
                cancel: false,
                cancelAsync: null,
            }

            this.options.onTabSelecting(eventArgs)

            return {
                cancel: eventArgs.cancel,
                cancelAsync: eventArgs.cancelAsync
            }
        }

        return null
    }

    private onTabSelected(previousTabName: string, currentTabName: string): void {
        if (this.options != null && _.isFunction(this.options.onTabSelected)) {
            this.options.onTabSelected({
                component: this,
                previousTabName,
                currentTabName
            })
        }
    }

    //#endregion


    //#region IIMRTabs Implementation

    public get currentTabName(): string {
        return this.currentActiveTabItem != null ? this.currentActiveTabItem.name : null
    }

    //#endregion


    //#region Parent/Child

    public registerTab(tabController: IMRTabController): void {
        this.tabControllers.push(tabController)
    }

    public getNextTabIndex(): number {
        return this.tabCount++
    }

    //#endregion


    //#region Tabs

    public tabsOptions: DevExpress.ui.dxTabsOptions
    private tabs: DevExpress.ui.dxTabs
    private currentActiveTabItem: TabItem

    public initializeTabs(): JQueryPromise<void> {
        return this
            .initializeIMRTabs()
            .then(() => {
                this.setActiveTabItemByIndex(this.getState().selectedTabIndex)

                this.currentActiveTabItem = this.getActiveTabItem()

                if (this.currentActiveTabItem == null) {
                    const tabController: IMRTabController = _.find(this.tabControllers, (tab: IMRTabController) => tab.isVisible == true)

                    if (tabController != null) {
                        this.setActiveTabItemByIndex(tabController.getTabItem().index)
                        this.currentActiveTabItem = this.getActiveTabItem()
                    }
                }

                return this.initializeDXItem()
            })
    }

    private initializeIMRTabs(): JQueryPromise<void> {
        if (_.size(this.tabControllers) > 0) {
            const tabControllersReady = _.map(this.tabControllers, controller => controller.isInitialized)

            return $.when(...tabControllersReady).then(() => { })
        }

        return $.Deferred<void>().resolve().promise()
    }

    private initializeDXItem() {
        this.tabsOptions = {
            selectedIndex: this.currentActiveTabItem != null ? this.currentActiveTabItem.index : 0,

            dataSource: this.tabControllers.map((tabController: IMRTabController) => {
                return tabController.getTabItem()
            }),

            elementAttr: {
                class: this.options != null ? this.options.tabsCSSClass : null,
            },

            onInitialized: (e: any) => {
                this.tabs = e.component
            },

            onItemRendered: (e: any) => {
                e.itemElement.on("dxpointerdown", (evnt) => {
                    this.selectTabItem(e.itemData)
                    evnt.preventDefault()
                })
            },

            onContentReady: (e: any) => {
                setTimeout(() => {
                    e.element.off("dxclick");
                }, 0)
            }
        }

        return TypeHelper.onInitializedAll(this.tabsOptions)
    }

    private toggleTabsDisabled(isDisabled: boolean = false): void {
        this.tabs.option('disabled', isDisabled)
    }

    private getActiveTabItem(): TabItem {
        const activetController = _.find(this.tabControllers, (controller: IMRTabController) => {
            return controller.getTabItem().active
        })

        if (activetController != null) {
            return activetController.getTabItem()
        }

        return null
    }

    private getCurrentTabIndex(): number {
        return this.tabs.option('selectedIndex')
    }

    private setActiveTabItemByIndex(index: number): void {
        _.forEach(this.tabControllers, (controller: IMRTabController) => {
            if (controller.getTabItem().index === index && controller.getTabItem().visible) {
                controller.setActive()
                this.saveState({
                    selectedTabIndex: index
                })
            } else {
                controller.setInactive()
            }
        })
    }

    private setCurrentTabIndex(index: number): void {
        this.tabs.option('selectedIndex', index)
    }

    //#endregion


    //#region Tabs Selection

    private selectTabItem(tabItem: TabItem): void {
        if (tabItem != null) {
            const cancelResults = this.onTabSelecting(tabItem.name, this.currentActiveTabItem.name)

            if (cancelResults != null && cancelResults.cancelAsync) {
                this.toggleTabsDisabled(true)

                cancelResults.cancelAsync.then((isCanceled: boolean) => {
                    if (!isCanceled) {
                        this.setCurrentTabIndex(tabItem.index)
                        this.onTabSelected(this.currentActiveTabItem.name, tabItem.name)
                        this.setActiveTabItemByIndex(this.getCurrentTabIndex())
                        this.currentActiveTabItem = this.getActiveTabItem()
                    }

                    this.toggleTabsDisabled(false)
                })
            } else if (cancelResults == null || !cancelResults.cancel) {
                this.setCurrentTabIndex(tabItem.index)
                this.onTabSelected(this.currentActiveTabItem.name, tabItem.name)
                this.setActiveTabItemByIndex(this.getCurrentTabIndex())
                this.currentActiveTabItem = this.getActiveTabItem()
            }
        }
    }

    //#endregion


    //#region State

    private saveState(state: any): void {
        if (state != null) {
            this.onStateStoring({
                selectedTabIndex: state.selectedTabIndex || 0
            })
        }
    }

    private getState(): IMRTabsState {
        const state: IMRTabsState = this.onStateRestoring()

        return _.defaults({}, state, {
            selectedTabIndex: 0
        })
    }

    //#endregion
}
